/**
 * Which competition the surrounding screens belong to (specs/competitions-hub-client Slice 3).
 *
 * Replaces ~20 direct `WORLD_CUP_COMPETITION_ID` imports across the squad/leaderboard
 * components. Every query one level down already takes `competitionId` as its first
 * argument, so components only need to know *which* competition they render.
 */

import { createContext, useContext, type ReactNode } from 'react';
import {
  WORLD_CUP_COMPETITION_CONTEXT,
  type CompetitionContextValue,
  type CompetitionSurface,
} from './competitionContext.constants';

const CompetitionContext = createContext<CompetitionContextValue | null>(null);

interface CompetitionProviderProps {
  value: CompetitionContextValue;
  children: ReactNode;
}

export function CompetitionProvider({ value, children }: CompetitionProviderProps) {
  return <CompetitionContext.Provider value={value}>{children}</CompetitionContext.Provider>;
}

/**
 * Never throws. With no provider mounted it returns the frozen World Cup value, so
 * stories/tests/global overlays keep behaving exactly as before this context existed.
 */
export function useCompetitionContext(): CompetitionContextValue {
  return useContext(CompetitionContext) ?? WORLD_CUP_COMPETITION_CONTEXT;
}

export function useCompetitionId(): string {
  return useCompetitionContext().competitionId;
}

export function useCompetitionBasePath(): string {
  return useCompetitionContext().basePath;
}

export function useCompetitionSurface(): CompetitionSurface {
  return useCompetitionContext().surface;
}
